import { useState, useEffect } from 'react';
import { DeepLinkConfig } from '../services/DeepLinkService.ts';

export interface DealMemoData {
  dealType: 'text' | 'image';
  content?: string;
  imageUrls?: {
    mobile: string;
    desktop: string;
  };
  deepLink?: DeepLinkConfig | null;
  lastEdited: Date | null;
  lastAuthor: string;
  editors: string[];
  isPublished: boolean;
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

export const useDeals = (dealId: string | null) => {
  const [deals, setDeals] = useState<DealMemoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) {
      setDeals(null);
      setLoading(false);
      return;
    }

    const fetchDeals = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/deals/${dealId}`);
        const result = await response.json();

        if (response.ok && result.success && result.data) {
          // Filter out template text as an extra safety measure (but only for text deals)
          const isTemplateContent = result.data.dealType === 'text' && result.data.content && (
            result.data.content.includes('Enter your deals content here') ||
            result.data.content.includes('Enter your deal content here') ||
            result.data.content === '<p>Enter your deals content here...</p>' ||
            result.data.content === '<p></p>' ||
            result.data.content === ''
          );

          if (isTemplateContent) {
            setDeals(null);
          } else {
            // Backend returns only published deals, so we create the structure expected by the frontend
            setDeals({
              dealType: result.data.dealType || 'text',
              content: result.data.content,
              imageUrls: result.data.imageUrls,
              deepLink: result.data.deepLink || null,
              lastEdited: null, // Backend doesn't return metadata for published deals
              lastAuthor: '',
              editors: [],
              isPublished: true
            });
          }
        } else {
          setDeals(null);
        }
      } catch (error) {
        console.error('Error fetching deals from backend API:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch deals');
        setDeals(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [dealId]);

  return { deals, loading, error };
};